/**
 * Minimalist data-center / digital-infrastructure motifs.
 * All SVGs use currentColor + hairline strokes; pass className for color.
 */

export function StatusLED({ className = "", color = "#0F1117" }) {
  return (
    <span className={`relative inline-flex items-center justify-center ${className}`} aria-hidden>
      <span
        className="absolute inline-block w-3 h-3 rounded-full opacity-30 animate-ping"
        style={{ backgroundColor: color }}
      />
      <span
        className="relative inline-block w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
      />
    </span>
  );
}

export function CrossMark({ className = "" }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M6 0V12M0 6H12"
        stroke="currentColor"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Blueprint-style corner brackets — wrap any positioned container. */
export function CornerMarks({ color = "#A9ADB4" }) {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      <span className="absolute top-0 left-0 w-3 h-3 border-l border-t" style={{ borderColor: color }} />
      <span className="absolute top-0 right-0 w-3 h-3 border-r border-t" style={{ borderColor: color }} />
      <span className="absolute bottom-0 left-0 w-3 h-3 border-l border-b" style={{ borderColor: color }} />
      <span className="absolute bottom-0 right-0 w-3 h-3 border-r border-b" style={{ borderColor: color }} />
    </div>
  );
}

/**
 * Server-rack elevation diagram.
 * 24U enclosure with realistic server units (drive bays, vents, status LEDs,
 * mounting screws), a top header panel, side mounting rails, and a bottom PDU.
 */
export function RackDiagram({ className = "" }) {
  // Layout — viewBox uses generous units for crisp detail
  const W = 80;
  const UNITS = 24;
  const rowH = 8;
  const headerH = 14;
  const footerH = 10;
  const railX = 6;
  const railW = W - railX * 2;
  const rackTop = headerH;
  const total = UNITS * rowH;
  const H = headerH + total + footerH + 4;

  // Server installations — { startU (top to bottom), height (in U), kind, leds }
  // U.01 at the very top, U.24 at the bottom.
  const SERVERS = [
    { u: 1, h: 2, kind: "compute", leds: ["green", "green"] }, // dual-PSU top server
    { u: 4, h: 3, kind: "storage", leds: ["green", "amber"] }, // storage with drive bays
    { u: 8, h: 1, kind: "switch", leds: ["green"] },           // network switch
    { u: 11, h: 2, kind: "compute", leds: ["green", "green"] },
    { u: 16, h: 1, kind: "patch", leds: [] },                 // patch panel
    { u: 19, h: 4, kind: "gpu", leds: ["green", "green", "amber"] }, // GPU chassis
  ];

  const yAt = (u) => rackTop + (u - 1) * rowH;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={className}
      fill="none"
      aria-hidden
    >
      <defs>
        {/* Chrome rail gradient */}
        <linearGradient id="rack-rail" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0F1117" stopOpacity="0.55" />
          <stop offset="50%" stopColor="#A9ADB4" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0F1117" stopOpacity="0.55" />
        </linearGradient>
        {/* Server bezel — slight gradient for depth */}
        <linearGradient id="rack-server" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1A1C20" />
          <stop offset="50%" stopColor="#0F1117" />
          <stop offset="100%" stopColor="#000000" />
        </linearGradient>
      </defs>

      {/* Outer frame */}
      <rect
        x="0.6"
        y="0.6"
        width={W - 1.2}
        height={H - 1.2}
        rx="1"
        stroke="currentColor"
        strokeOpacity="0.5"
        strokeWidth="0.6"
        vectorEffect="non-scaling-stroke"
      />

      {/* Header panel (cabinet top, name plate, status LEDs) */}
      <rect
        x={railX - 2}
        y="2"
        width={W - (railX - 2) * 2}
        height={headerH - 4}
        fill="#F4F5F7"
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeWidth="0.5"
        vectorEffect="non-scaling-stroke"
      />
      {/* Header LEDs */}
      <circle cx={W - 8} cy="7" r="0.8" fill="#22C55E" />
      <circle cx={W - 11.5} cy="7" r="0.8" fill="#E5C36A" />
      <circle cx={W - 15} cy="7" r="0.8" fill="currentColor" fillOpacity="0.3" />
      {/* Header name plate (rack identifier) */}
      <text
        x={railX}
        y="8.4"
        fontSize="3.4"
        fill="currentColor"
        fillOpacity="0.7"
        fontFamily="Space Mono, monospace"
        letterSpacing="0.4"
      >
        DCYB · A12
      </text>

      {/* Mounting rails (left/right) */}
      <rect x={railX} y={rackTop} width="1.5" height={total} fill="url(#rack-rail)" />
      <rect x={W - railX - 1.5} y={rackTop} width="1.5" height={total} fill="url(#rack-rail)" />

      {/* U division lines — every U */}
      {Array.from({ length: UNITS + 1 }).map((_, i) => (
        <line
          key={`u-${i}`}
          x1={railX + 1.5}
          x2={W - railX - 1.5}
          y1={rackTop + i * rowH}
          y2={rackTop + i * rowH}
          stroke="currentColor"
          strokeOpacity="0.12"
          strokeWidth="0.4"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* U numbers on left rail */}
      {[1, 6, 12, 18, 24].map((u) => (
        <text
          key={`unum-${u}`}
          x={railX - 1}
          y={yAt(u) + rowH / 2 + 1.2}
          fontSize="2.8"
          textAnchor="end"
          fill="currentColor"
          fillOpacity="0.55"
          fontFamily="Space Mono, monospace"
        >{`${String(u).padStart(2, "0")}`}</text>
      ))}

      {/* Server units */}
      {SERVERS.map((s, idx) => (
        <ServerUnit
          key={`srv-${idx}`}
          x={railX + 2}
          y={yAt(s.u) + 0.6}
          w={railW - 4}
          h={s.h * rowH - 1.2}
          kind={s.kind}
          leds={s.leds}
        />
      ))}

      {/* Bottom PDU strip */}
      <rect
        x={railX - 2}
        y={H - footerH - 2}
        width={W - (railX - 2) * 2}
        height={footerH - 4}
        fill="#0F1117"
        fillOpacity="0.92"
      />
      {/* PDU outlets — small circles */}
      {Array.from({ length: 8 }).map((_, i) => (
        <circle
          key={`pdu-${i}`}
          cx={railX + 2 + i * ((W - (railX) * 2 - 4) / 7)}
          cy={H - footerH + 1}
          r="0.85"
          fill="#F4F5F7"
          fillOpacity="0.8"
        />
      ))}
      {/* PDU LED */}
      <circle cx={W - 4} cy={H - footerH + 1} r="0.9" fill="#22C55E" />

      {/* Mounting feet */}
      <rect x="2" y={H - 3} width="3" height="2" fill="currentColor" fillOpacity="0.6" />
      <rect x={W - 5} y={H - 3} width="3" height="2" fill="currentColor" fillOpacity="0.6" />
    </svg>
  );
}

/** A single server unit inside the rack — front-bezel view. */
function ServerUnit({ x, y, w, h, kind, leds = [] }) {
  // Mounting screws — 4 corners
  const screwR = 0.4;
  const screws = [
    [x + 0.8, y + 0.8],
    [x + w - 0.8, y + 0.8],
    [x + 0.8, y + h - 0.8],
    [x + w - 0.8, y + h - 0.8],
  ];

  return (
    <g>
      {/* Body */}
      <rect x={x} y={y} width={w} height={h} fill="url(#rack-server)" />
      {/* Subtle highlight on top */}
      <rect x={x} y={y} width={w} height="0.4" fill="#FFFFFF" fillOpacity="0.15" />

      {/* Kind-specific detail */}
      {kind === "compute" && <ComputeDetail x={x} y={y} w={w} h={h} />}
      {kind === "storage" && <StorageDetail x={x} y={y} w={w} h={h} />}
      {kind === "switch" && <SwitchDetail x={x} y={y} w={w} h={h} />}
      {kind === "gpu" && <GpuDetail x={x} y={y} w={w} h={h} />}
      {kind === "patch" && <PatchDetail x={x} y={y} w={w} h={h} />}

      {/* Status LEDs along right side */}
      {leds.map((color, i) => (
        <circle
          key={`led-${i}`}
          cx={x + w - 2.5}
          cy={y + 1.6 + i * 1.6}
          r="0.5"
          fill={
            color === "green"
              ? "#22C55E"
              : color === "amber"
                ? "#E5C36A"
                : "#71747B"
          }
        />
      ))}

      {/* Mounting screws */}
      {screws.map(([cx, cy], i) => (
        <circle key={`screw-${i}`} cx={cx} cy={cy} r={screwR} fill="#F4F5F7" fillOpacity="0.55" />
      ))}
    </g>
  );
}

/* ---- Server bezel detail variants ---- */
function ComputeDetail({ x, y, w, h }) {
  // Center vent slats + small drive bay rectangles on the left
  const ventX1 = x + w * 0.32;
  const ventX2 = x + w * 0.62;
  const slats = Math.max(3, Math.floor(h / 1.5));
  return (
    <g>
      {/* Drive bays (small rectangles, left side) */}
      {[0, 1, 2].map((i) => (
        <rect
          key={`bay-${i}`}
          x={x + 2 + i * 2.4}
          y={y + h / 2 - 1}
          width="1.9"
          height="2"
          fill="#1A1C20"
          stroke="#F4F5F7"
          strokeOpacity="0.35"
          strokeWidth="0.25"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {/* Center vent slats */}
      {Array.from({ length: slats }).map((_, i) => (
        <line
          key={`vent-${i}`}
          x1={ventX1}
          x2={ventX2}
          y1={y + 1.6 + (i * (h - 3.2)) / Math.max(1, slats - 1)}
          y2={y + 1.6 + (i * (h - 3.2)) / Math.max(1, slats - 1)}
          stroke="#F4F5F7"
          strokeOpacity="0.18"
          strokeWidth="0.35"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </g>
  );
}

function StorageDetail({ x, y, w, h }) {
  // Grid of disk drive bays
  const cols = 8;
  const rows = Math.max(2, Math.floor(h / 2.2));
  const cellW = (w - 6) / cols;
  const cellH = (h - 2.4) / rows;
  return (
    <g>
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: cols }).map((_, c) => (
          <rect
            key={`disk-${r}-${c}`}
            x={x + 3 + c * cellW + 0.15}
            y={y + 1.2 + r * cellH + 0.15}
            width={cellW - 0.3}
            height={cellH - 0.3}
            fill="#1A1C20"
            stroke="#F4F5F7"
            strokeOpacity="0.32"
            strokeWidth="0.22"
            vectorEffect="non-scaling-stroke"
          />
        ))
      )}
    </g>
  );
}

function SwitchDetail({ x, y, w, h }) {
  // Row of RJ45-style port squares
  const ports = 12;
  const startX = x + 4;
  const portsW = w - 10;
  const cellW = portsW / ports;
  return (
    <g>
      {/* Switch label dot */}
      <rect x={x + 1.5} y={y + h / 2 - 0.7} width="1.5" height="1.4" fill="#22C55E" fillOpacity="0.7" />
      {/* Ports */}
      {Array.from({ length: ports }).map((_, i) => (
        <rect
          key={`port-${i}`}
          x={startX + i * cellW + 0.15}
          y={y + h / 2 - 0.9}
          width={cellW - 0.3}
          height="1.8"
          fill="#1A1C20"
          stroke="#F4F5F7"
          strokeOpacity="0.4"
          strokeWidth="0.2"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </g>
  );
}

function GpuDetail({ x, y, w, h }) {
  // Multiple horizontal GPU sleds + heavy vent grills on the right
  const sleds = 4;
  return (
    <g>
      {/* GPU sleds */}
      {Array.from({ length: sleds }).map((_, i) => (
        <rect
          key={`sled-${i}`}
          x={x + 2}
          y={y + 1.5 + i * ((h - 3) / sleds)}
          width={w * 0.55}
          height={(h - 3) / sleds - 0.6}
          fill="#1A1C20"
          stroke="#F4F5F7"
          strokeOpacity="0.3"
          strokeWidth="0.25"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {/* Vent grill (right) */}
      {Array.from({ length: Math.max(4, Math.floor(h / 1.1)) }).map((_, i) => (
        <line
          key={`gv-${i}`}
          x1={x + w * 0.6}
          x2={x + w - 3.5}
          y1={y + 1 + i * 1.1}
          y2={y + 1 + i * 1.1}
          stroke="#F4F5F7"
          strokeOpacity="0.22"
          strokeWidth="0.3"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </g>
  );
}

function PatchDetail({ x, y, w, h }) {
  // Patch panel — row of small fiber/cable ports
  const ports = 24;
  const startX = x + 3;
  const portsW = w - 8;
  const cellW = portsW / ports;
  return (
    <g>
      {Array.from({ length: ports }).map((_, i) => (
        <rect
          key={`p-${i}`}
          x={startX + i * cellW + 0.05}
          y={y + h / 2 - 0.7}
          width={cellW - 0.1}
          height="1.4"
          fill="#1A1C20"
          stroke="#F4F5F7"
          strokeOpacity="0.45"
          strokeWidth="0.18"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </g>
  );
}

/** Fiber fan-out from one anchor to many endpoints. */
export function FiberFanout({ className = "" }) {
  return (
    <svg viewBox="0 0 80 60" className={className} fill="none" aria-hidden>
      <circle cx="8" cy="30" r="2" fill="currentColor" />
      {[8, 18, 30, 42, 52].map((y, i) => (
        <path
          key={`path-${y}`}
          d={`M 10 30 C 30 30, 40 ${y}, 70 ${y}`}
          stroke="currentColor"
          strokeOpacity={0.55 - i * 0.04}
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {[8, 18, 30, 42, 52].map((y) => (
        <circle key={`dot-${y}`} cx="72" cy={y} r="1.5" fill="currentColor" fillOpacity="0.8" />
      ))}
    </svg>
  );
}

/** Power / flow diagram with arrowed segments. */
export function FlowDiagram({ className = "" }) {
  return (
    <svg viewBox="0 0 80 40" className={className} fill="none" aria-hidden>
      <rect x="2" y="14" width="14" height="12" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <line x1="16" x2="32" y1="20" y2="20" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <rect x="32" y="14" width="14" height="12" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <line x1="46" x2="62" y1="20" y2="20" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <rect x="62" y="14" width="14" height="12" fill="currentColor" />
      <polygon points="30,18 32,20 30,22" fill="currentColor" />
      <polygon points="60,18 62,20 60,22" fill="currentColor" />
    </svg>
  );
}

/** Bridge / interconnect — two stacks linked by a span. */
export function BridgeDiagram({ className = "" }) {
  return (
    <svg viewBox="0 0 80 40" className={className} fill="none" aria-hidden>
      <rect x="4" y="6" width="14" height="28" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <rect x="62" y="6" width="14" height="28" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <line x1="11" x2="11" y1="6" y2="34" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <line x1="69" x2="69" y1="6" y2="34" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <path d="M 18 20 C 35 4, 45 4, 62 20" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <circle cx="18" cy="20" r="1.5" fill="currentColor" />
      <circle cx="62" cy="20" r="1.5" fill="currentColor" />
    </svg>
  );
}

/** Tiny inline sparkline — gradient area + crisp line. */
export function Sparkline({
  data = [4, 7, 6, 9, 12, 14, 18, 22],
  className = "",
  trend = "up",
}) {
  const W = 70;
  const H = 22;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const step = W / (data.length - 1);
  const points = data.map((v, i) => {
    const x = i * step;
    const y = H - ((v - min) / span) * (H - 4) - 2;
    return [x, y];
  });
  const line = points.map(([x, y], i) => `${i ? "L" : "M"} ${x} ${y}`).join(" ");
  const area = `${line} L ${W} ${H} L 0 ${H} Z`;
  const accent = trend === "down" ? "#71747B" : "#0F1117";
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={className}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={`sl-area-${trend}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sl-area-${trend})`} />
      <path
        d={line}
        fill="none"
        stroke={accent}
        strokeWidth="1.25"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="1.8"
          fill={accent}
        />
      )}
    </svg>
  );
}

/** Horizontal gauge bar with chrome fill. */
export function GaugeBar({ value = 50, max = 100, className = "" }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={`h-[3px] bg-[#E3E5E9] relative overflow-hidden ${className}`} aria-hidden>
      <div className="h-full chrome-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Terminal / cohort node — clustered dots inside frame. */
export function NodeCluster({ className = "" }) {
  return (
    <svg viewBox="0 0 80 40" className={className} fill="none" aria-hidden>
      <rect x="0.5" y="0.5" width="79" height="39" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      {[
        [14, 12], [26, 12], [38, 12], [50, 12], [62, 12],
        [14, 20], [26, 20], [38, 20], [50, 20], [62, 20],
        [14, 28], [26, 28], [38, 28], [50, 28], [62, 28],
      ].map(([x, y], i) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" fill="currentColor" fillOpacity={i % 4 === 0 ? 1 : 0.35} />
      ))}
    </svg>
  );
}
